import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SoundProvider } from "@/context/SoundContext";
import { useEffect, useState } from "react";
import { SplashScreen } from "@/components/SplashScreen";
import { InstallPrompt } from "@/components/InstallPrompt";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TommyChat from "@/components/TommyChat";
import LandingPage from "@/pages/LandingPage";
import BrowseAgents from "@/pages/BrowseAgents";
import BrowseJobs from "@/pages/BrowseJobs";
import AgentDetail from "@/pages/AgentDetail";
import JobDetail from "@/pages/JobDetail";
import Dashboard from "@/pages/Dashboard";
import AuthPage from "@/pages/AuthPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PostAgent from "@/pages/PostAgent";
import PostJob from "@/pages/PostJob";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import RefundPolicy from "@/pages/RefundPolicy";
import FAQ from "@/pages/FAQ";
import APIPage from "@/pages/APIPage";
import HouseAgentSuccess from "@/pages/HouseAgentSuccess";
import HouseAgentCancel from "@/pages/HouseAgentCancel";
import TrustCenter from "@/pages/TrustCenter";
import About from "@/pages/About";
import AgentBuilderWizard from "@/pages/AgentBuilderWizard";
import MyAgents from "@/pages/MyAgents";
import WebsiteBuilder from "@/pages/WebsiteBuilder";
import MyWebsites from "@/pages/MyWebsites";
import MyOrders from "@/pages/MyOrders";
import OperationsDashboard from "@/pages/OperationsDashboard";
import ChatbotBuilder from "@/pages/ChatbotBuilder";
import MyChatbots from "@/pages/MyChatbots";
import ChatbotChat from "@/pages/ChatbotChat";
import TaskTemplates from "@/pages/TaskTemplates";
import AIClone from "@/pages/AIClone";
import ForRealtors from "@/pages/verticals/ForRealtors";
import ForCreators from "@/pages/verticals/ForCreators";
import WhiteLabel from "@/pages/verticals/WhiteLabel";
import PricingPage from "@/pages/PricingPage";
import UserDashboard from "@/pages/UserDashboard";
import ReferralPage from "@/pages/ReferralPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OnboardingTour from "@/components/OnboardingTour";
import AgentMemory from "@/pages/AgentMemory";
import TemplateMarketplace from "@/pages/TemplateMarketplace";
import BrowserAgent from "@/pages/BrowserAgent";
import IntegrationHub from "@/pages/IntegrationHub";
import ProductArchitect from "@/pages/ProductArchitect";
import VideoGallery from "@/pages/VideoGallery";
import OwnerAdmin from "@/pages/OwnerAdmin";
import Contact from "@/pages/Contact";
import Showcase from "@/pages/Showcase";
import WorkflowsPage from "@/pages/WorkflowsPage";
import PublicSite from "@/pages/PublicSite";
import WebsiteBuilderPro from "@/pages/WebsiteBuilderPro";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Immediate scroll - multiple approaches for reliability
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also scroll after content loads
    const timer1 = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 50);
    
    // And again after any async content
    const timer2 = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    }, 150);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [pathname]);
  
  return null;
}

function AppContent() {
  const [showSplash, setShowSplash] = useState(() => {
    // Only show splash on first visit or if more than 1 hour since last visit
    const lastVisit = localStorage.getItem('ttc_last_visit');
    const now = Date.now();
    if (!lastVisit || (now - parseInt(lastVisit)) > 3600000) {
      return true;
    }
    return false;
  });

  const handleSplashComplete = () => {
    setShowSplash(false);
    localStorage.setItem('ttc_last_visit', Date.now().toString());
  };

  return (
    <>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </>
  );
}

// Separate component to use useLocation inside BrowserRouter
function AppRoutes() {
  const location = useLocation();
  const isPublicSite = location.pathname.startsWith('/s/');

  // Public site pages render without navbar/footer
  if (isPublicSite) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <Routes>
          <Route path="/s/:slug" element={<PublicSite />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] flex flex-col">
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      <Navbar />
      <main id="main-content" className="flex-1" role="main">
        <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/agents" element={<BrowseAgents />} />
              <Route path="/agents/:id" element={<AgentDetail />} />
              <Route path="/jobs" element={<BrowseJobs />} />
              <Route path="/jobs/:id" element={<JobDetail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/post-agent" element={<PostAgent />} />
              <Route path="/post-job" element={<PostJob />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/subscription/cancel" element={<SubscriptionPage />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/developers" element={<APIPage />} />
              <Route path="/house-agent/success" element={<HouseAgentSuccess />} />
              <Route path="/house-agent/cancel" element={<HouseAgentCancel />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/trust" element={<TrustCenter />} />
              <Route path="/trust-center" element={<TrustCenter />} />
              <Route path="/about" element={<About />} />
              <Route path="/ops" element={<OperationsDashboard />} />
              <Route path="/chatbot-builder" element={<ChatbotBuilder />} />
              <Route path="/my-chatbots" element={<MyChatbots />} />
              <Route path="/chat/:publicId" element={<ChatbotChat />} />
              <Route path="/templates" element={<TaskTemplates />} />
              <Route path="/ai-clone" element={<AIClone />} />
              <Route path="/for-realtors" element={<ForRealtors />} />
              <Route path="/for-creators" element={<ForCreators />} />
              <Route path="/white-label" element={<WhiteLabel />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/user-dashboard" element={<UserDashboard />} />
              <Route path="/referral" element={<ReferralPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/agent-memory" element={<AgentMemory />} />
              <Route path="/marketplace" element={<TemplateMarketplace />} />
              <Route path="/browser-agent" element={<BrowserAgent />} />
              <Route path="/integrations" element={<IntegrationHub />} />
              <Route path="/architect" element={<ProductArchitect />} />
              <Route path="/video-gallery" element={<VideoGallery />} />
              <Route path="/owner-admin" element={<OwnerAdmin />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/showcase" element={<Showcase />} />
              <Route path="/workflows" element={<WorkflowsPage />} />
              <Route path="/create-agent" element={<AgentBuilderWizard />} />
              <Route path="/my-agents" element={<MyAgents />} />
              <Route path="/website-builder" element={<WebsiteBuilder />} />
              <Route path="/website-builder-pro" element={<WebsiteBuilderPro />} />
              <Route path="/my-websites" element={<MyWebsites />} />
            </Routes>
          </main>
          <Footer />
          <TommyChat />
          <InstallPrompt />
          <OnboardingTour />
        </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SoundProvider>
        <AppContent />
      </SoundProvider>
    </AuthProvider>
  );
}

export default App;
